import { ChevronDownIcon, ChevronUpIcon, CogIcon } from '@heroicons/react/24/solid';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { RootState } from '../../../store/store';
import { getBrandingReferentMappings } from '../../../utils/api';
import { getValidReferents } from '../../../utils/branding';
import { coproTypes } from '../../../utils/copros';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { calculateTotalSurface, calculateValidatedBuildings } from '../../Setup/StepCoproArea/utils';
import Badge from '../Badge';
import FileUpload from '../FileUpload/FileUpload.module';
import { useFileUpload } from '../FileUpload/useFileUpload';
import MUITable from '../MUITable';
import { showToast } from '../Notification';
import Popup from '../Popup/Popup.module';
import RedirectionCoproButtons from '../RedirectionCoproButtons';
import RedirectionMapButtons from '../RedirectionMapButtons';
import Spinner from '../Spinner';
import ReferentPresentationUploadList from './ReferentPresentationUploadList';

const headers = [
  { title: 'N° IMMAT' },
  { title: 'NOM' },
  { title: 'ADRESSE' },
  { title: 'NB LOTS HAB / LOCAL PRO' },
  { title: 'TYPE' },
  { title: 'NB BATIMENTS' },
  { title: 'SURFACE TOTALE (m²)' },
  { title: 'NB DPEs' },
  { title: 'ACTIONS' },
];

const sortableColumns = [
  'N° IMMAT', 'NOM', 'ADRESSE', 'NB LOTS HAB / LOCAL PRO', 'TYPE', 'NB BATIMENTS', 'SURFACE TOTALE (m²)', 'NB DPEs',
];

const MAX_FILE_SIZE = 5;

// Must match ReportService::ALLOWED_SALES_RADII on the backend
const SALES_RADIUS_OPTIONS = [150, 250, 500, 750, 1000];
const DEFAULT_SALES_RADIUS = 150;

interface ReportGeneratorProps {
  description: string;
  getReportUrl: (companyId: string, coproId: string) => string;
  generatedPdfFileName?: string;
  initialCoproId?: number;
  /** Shows the "Options avancées" block letting the user pick the radius of the surrounding sales. */
  hasSalesRadiusOption?: boolean;
}

const sanitizeFileName = (fileName: string): string => fileName
  .replaceAll(/[^a-z0-9]/gi, '_') // Replace non-alphanumeric characters with underscores
  .replaceAll(/_+/g, '_') // Replace multiple consecutive underscores with a single one
  .toLowerCase()
  .trim();

const ReportGenerator: React.FC<ReportGeneratorProps> = ({
  description,
  getReportUrl,
  generatedPdfFileName = 'rapport-copro.pdf',
  initialCoproId = null,
  hasSalesRadiusOption = false,
}) => {
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [copros, setCopros] = useState([]);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedCopro, setSelectedCopro] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [selectedCoproPresentation, setSelectedCoproPresentation] = useState(null);
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [currentPdfPreviewUrl, setCurrentPdfPreviewUrl] = useState<string | null>(null);
  const [currentPdfPreviewTitle, setCurrentPdfPreviewTitle] = useState<string>('');
  const [isLoadingCoproPdf, setIsLoadingCoproPdf] = useState(false);
  const [coproBrandingReferentMap, setCoproBrandingReferentMap] = useState<Map<number, string>>(new Map());
  const [isAdvancedOptionsOpen, setIsAdvancedOptionsOpen] = useState(false);
  const [salesRadius, setSalesRadius] = useState<number>(DEFAULT_SALES_RADIUS);

  const brandingReferents = useMemo(() => getValidReferents(company), [company]);

  useEffect(() => {
    if (!initialCoproId || !copros) {return;}
    setSelectedCopro(copros.find((copro) => copro.id === initialCoproId));
  }, [initialCoproId, copros]);

  const fetchData = useCallback(async() => {
    setStatus('PENDING');
    try {
      const [coprosResponse, buildingsResponse, dpesResponse] = await Promise.all([
        fetch(`/api/companies/${company.id}/copros?limit=1000`, JWTHeader(jwt)),
        fetch(`/api/companies/${company.id}/copros/buildings`, JWTHeader(jwt)),
        fetch(`/api/companies/${company.id}/copros/dpes/kpi`, JWTHeader(jwt)),
      ]);

      const coprosData = await coprosResponse.json();
      const buildingsData = await buildingsResponse.json();
      const dpesData = await dpesResponse.json();

      if (coprosData.results) {
        const coprosWithBuildings = coprosData.results.map((copro) => {
          const coproBuildings = buildingsData.find((b) => b.id === copro.id)?.buildings || [];
          const mappedBuildings = coproBuildings.map((building) => ({
            id: building.id,
            batimentId: building.remoteId,
            realFloorCount: building.floors || 0,
            realSurface: building.area || '',
            isValidatedByUser: building.validatedByUser ?? null,
          }));

          const coproDpes = dpesData.find((d) => d.id === copro.id)?.kpi || {};
          const dpeCount = Object.values(coproDpes).reduce((sum: number, count: number) => sum + count, 0);

          return {
            ...copro,
            batiments: mappedBuildings,
            dpeCount,
          };
        });

        setCopros(coprosWithBuildings);
      } else {
        setCopros([]);
        throw new Error('No copros found');
      }

      setStatus('SUCCESS');
    } catch (error) {
      console.error('Error fetching data :', error);
      setStatus('ERROR');
      setErrorMessage('Erreur lors du chargement des données');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  }, [company, jwt]);

  useEffect(() => {
    if (!company || !jwt || !company.id) {return;}
    fetchData();
  }, [company, jwt, fetchData]);

  useEffect(() => {
    if (!company?.id || !jwt) { return; }
    fetch(getBrandingReferentMappings(company.id), {
      headers: { Authorization: JWTAuthorization(jwt) },
    })
      .then((response) => response.json())
      .then((data) => {
        const mappings: { coproId: number; brandingReferentId: string }[] = Array.isArray(data) ? data : (data?.data ?? []);
        const map = new Map<number, string>();
        mappings.forEach((mapping) => {
          map.set(mapping.coproId, mapping.brandingReferentId);
        });
        setCoproBrandingReferentMap(map);
      })
      .catch((error) => {
        console.error('Error fetching branding referent mappings:', error);
      });
  }, [company?.id, jwt]);

  const fetchCoproPdf = async(coproId) => {
    // Find the copro from the copros array using coproId
    const copro = copros.find(c => c.id === coproId);
    
    // Skip if no reportSettings or if it's empty
    if (!copro?.reportSettings || Object.keys(copro.reportSettings).length === 0) {
      setSelectedCoproPresentation(null);
      return;
    }

    setIsLoadingCoproPdf(true);
    try {
      const response = await fetch(`/api/copros/${coproId}/report/settings/pdf-introduction`, {
        headers: {
          Authorization: JWTAuthorization(jwt),
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const file = new File([blob], 'presentation-de-la-copropriete.pdf', { type: 'application/pdf' });
        setSelectedCoproPresentation(file);
      } else {
        setSelectedCoproPresentation(null);
      }
    } catch (error) {
      console.error('Error fetching copro PDF :', error);
      setSelectedCoproPresentation(null);
    } finally {
      setIsLoadingCoproPdf(false);
    }
  };

  const handleDetailsClick = async(copro) => {
    if (selectedCopro?.id === copro.id) {
      setSelectedCopro(null);
      setSelectedCoproPresentation(null);
    } else {
      setSelectedCopro(copro);
      await fetchCoproPdf(copro.id);
    }
  };

  const handleGenerateReport = async(coproId: string, coproName: string) => {
    setIsGeneratingReport(true);
    try {
      const baseReportUrl = getReportUrl(company.id.toString(), coproId);
      const reportUrl = hasSalesRadiusOption ? `${baseReportUrl}?radius=${salesRadius}` : baseReportUrl;
      const response = await fetch(reportUrl, {
        method: 'POST',
        headers: {
          Authorization: JWTAuthorization(jwt),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      if (response.headers.get('X-Referent-Intro-Fallback') === '1') {
        showToast(
          'warning',
          'Aucun référent publique assigné à cette copropriété : la présentation du premier référent configuré a été utilisée.',
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      const sanitizedCoproName = sanitizeFileName(coproName);
      const fileName = `${generatedPdfFileName.replace('.pdf', '')}_${sanitizedCoproName}.pdf`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('success', 'Le rapport a été généré et téléchargé avec succès.');
    } catch (error) {
      console.error('Error generating report :', error);
      showToast('error', 'Une erreur est survenue lors de la génération du rapport.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleCoproPresentationUpload = async(file: File): Promise<boolean> => {
    if (!selectedCopro) {return false;}

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`/api/copros/${selectedCopro.id}/report/settings/pdf-introduction`, {
        method: 'POST',
        headers: {
          Authorization: JWTAuthorization(jwt),
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload copro presentation');
      }

      setCopros((prevCopros) => prevCopros.map((copro) => (copro.id === selectedCopro.id ? { ...copro, presentation: file } : copro)));
      setSelectedCoproPresentation(file);
      showToast('success', 'La présentation de la copropriété a été mise à jour avec succès.');
      return true;
    } catch (error) {
      console.error('Error uploading copro presentation :', error);
      showToast('error', 'Une erreur est survenue lors du téléchargement de la présentation de la copropriété.');
      return false;
    }
  };

  const handleDeleteCoproPresentation = async() => {
    if (!selectedCopro) {return;}

    try {
      const response = await fetch(`/api/copros/${selectedCopro.id}/report/settings/pdf-introduction`, {
        method: 'DELETE',
        headers: {
          Authorization: JWTAuthorization(jwt),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete copro presentation');
      }

      setCopros((prevCopros) => prevCopros.map((copro) => (copro.id === selectedCopro.id ? { ...copro, presentation: null } : copro)));
      setSelectedCoproPresentation(null);
      showToast('success', 'La présentation de la copropriété a été supprimée avec succès.');
    } catch (error) {
      console.error('Error deleting copro presentation :', error);
      showToast('error', 'Une erreur est survenue lors de la suppression de la présentation de la copropriété.');
    }
  };

  const coproFileUpload = useFileUpload({
    onFileSelect: handleCoproPresentationUpload,
    onFileDelete: handleDeleteCoproPresentation,
    acceptedFileTypes: '.pdf',
    maxFileSize: MAX_FILE_SIZE,
    currentFile: selectedCoproPresentation,
  });

  useEffect(() => {
    if (selectedCopro) {
      setSelectedCoproPresentation(selectedCopro.presentation || null);
    } else {
      setSelectedCoproPresentation(null);
    }
  }, [selectedCopro]);

  const handlePreviewPDF = async(url: string, setPreviewUrl: (url: string) => void, pdfTitle: string) => {
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: JWTAuthorization(jwt),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch PDF');
      }

      const blob = await response.blob();
      const pdfUrl = URL.createObjectURL(blob);
      setPreviewUrl(pdfUrl);
      setCurrentPdfPreviewUrl(pdfUrl);
      setCurrentPdfPreviewTitle(pdfTitle);
      setIsPdfPreviewOpen(true);
    } catch (error) {
      console.error('Error previewing PDF :', error);
      showToast('error', 'Une erreur est survenue lors de la prévisualisation du PDF.');
    }
  };

  const closePdfPreview = () => {
    setIsPdfPreviewOpen(false);
    setCurrentPdfPreviewUrl(null);
    setCurrentPdfPreviewTitle('');
  };

  const renderCoproDetails = (copro) => {
    const mappedReferentId = coproBrandingReferentMap.get(copro.id) ?? null;
    const mappedReferent = mappedReferentId
      ? brandingReferents.find((referent) => referent.id === mappedReferentId)
      : null;
    const mappedReferentLabel = mappedReferent
      ? [mappedReferent.title, mappedReferent.firstname, mappedReferent.lastname].filter(Boolean).join(' ')
      : null;

    return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3 }}
        className="rounded-b-[20px] border-x border-b px-4 py-6"
      >
        <div className="mb-4 flex items-center">
          <h2 className="mr-2 text-xl font-bold">
            Détails de la copropriété
            {' '}
            {copro.name}
          </h2>
          <RedirectionCoproButtons coproId={copro.id} type="circle" />
        </div>
        <div className="mb-6 md:grid md:grid-cols-2 md:gap-4">
          <div className="mb-4 md:mb-0">
            <h3 className="mb-2 font-semibold">Informations essentielles</h3>
            <p>
              N° IMMAT :
              {' '}
              {copro.remoteId}
            </p>
            <p>
              Nombre de lots :
              {' '}
              {copro.batchSize}
            </p>
            <p>
              Type :
              {' '}
              {coproTypes[copro.type] || 'Non spécifié'}
            </p>
            <p>
              Nombre de batiments :
              {' '}
              {calculateValidatedBuildings(copro) > 0
                ? calculateValidatedBuildings(copro)
                : copro.details?.detailsDonneestechniquesNbbatimentstotal || 'N/A'}
            </p>
            <p>
              Surface totale :
              {' '}
              {calculateTotalSurface(copro)}
            </p>
          </div>
          <div className="mb-4 md:mb-0">
            <h3 className="mb-2 font-semibold">Informations complémentaires</h3>
            <p>
              Date de fin de mandat :
              {' '}
              {copro.termEndAt ? new Date(copro.termEndAt).toLocaleDateString('fr-FR') : 'Non spécifié'}
            </p>
            <p>
              Date d'assemblée générale :
              {' '}
              {copro.generalMeetingApiDate ? new Date(copro.generalMeetingApiDate).toLocaleDateString('fr-FR') : 'Non spécifié'}
            </p>
            {copro.details?.detailsDonneestechniquesAnneeconstruction ? (
              <p>
                Année de construction :
                {' '}
                {copro.details.detailsDonneestechniquesAnneeconstruction}
              </p>
            ) : (
              <p>
                Période de construction :
                {' '}
                {
                  copro.details?.periodeDeConstruction || 'Non spécifié'
                }
              </p>
            )}
            <p>
              Nombre de DPEs :
              {' '}
              {copro.dpeCount || 0}
            </p>
          </div>
        </div>
        <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <h3 className="mb-2 font-semibold">Présentation du référent publique</h3>
          {mappedReferentLabel ? (
            <p className="text-sm text-slate-700">
              Le rapport utilisera la présentation PDF du référent publique assigné :
              {' '}
              <span className="font-medium">{mappedReferentLabel}</span>
              .
            </p>
          ) : (
            <p className="text-sm text-amber-800">
              Aucun référent publique assigné à cette copropriété — la présentation du premier référent configuré sera utilisée lors de la génération.
            </p>
          )}
        </div>
        <div className="mb-6">
          <h3 className="mb-2 font-semibold">Présentation de la copropriété</h3>
          {isLoadingCoproPdf ? (
            <Spinner aria-label="Loading copro PDF" />
          ) : (
            <FileUpload
              onFileSelect={coproFileUpload.handleFiles}
              onFileDelete={coproFileUpload.removeFile}
              acceptedFileTypes=".pdf"
              maxFileSize={MAX_FILE_SIZE}
              currentFile={coproFileUpload.file}
            />
          )}
          {coproFileUpload.file && (
            <button
              onClick={() => handlePreviewPDF(`/api/copros/${copro.id}/report/settings/pdf-introduction`, setCurrentPdfPreviewUrl, `Présentation de ${copro.name}`)}
              className="mt-2 w-full rounded border-2 border-rgpd-blue bg-transparent px-2 py-1 font-semibold text-rgpd-blue hover:bg-rgpd-blue hover:text-white md:w-auto"
            >
              Prévisualiser le PDF
            </button>
          )}
        </div>
        {hasSalesRadiusOption && (
          <div className="mb-6 rounded-lg border border-slate-200">
            <button
              type="button"
              className="flex w-full items-center justify-between px-4 py-3 text-left font-semibold"
              onClick={() => setIsAdvancedOptionsOpen((open) => !open)}
              aria-expanded={isAdvancedOptionsOpen}
            >
              Options avancées
              {isAdvancedOptionsOpen ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
            </button>
            {isAdvancedOptionsOpen && (
              <div className="border-t border-slate-200 px-4 py-4">
                <label htmlFor="report-sales-radius" className="mb-1 block text-sm font-medium text-slate-700">
                  Rayon des ventes autour de l'immeuble
                </label>
                <select
                  id="report-sales-radius"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm md:w-64"
                  value={salesRadius}
                  onChange={(event) => setSalesRadius(Number(event.target.value))}
                >
                  {SALES_RADIUS_OPTIONS.map((radius) => (
                    <option key={radius} value={radius}>
                      {radius >= 1000 ? `${radius / 1000} km` : `${radius} m`}
                      {radius === DEFAULT_SALES_RADIUS ? ' (par défaut)' : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-slate-500">
                  Élargissez le rayon lorsque peu de ventes sont recensées à proximité immédiate de l'immeuble.
                </p>
              </div>
            )}
          </div>
        )}
        <div className="flex items-center justify-end">
          <button
            className="hover:bg-rgpd-blue-dark-dark focus:shadow-outline w-full rounded bg-rgpd-blue-dark px-4 py-2 font-bold text-white focus:outline-none md:w-auto"
            onClick={() => handleGenerateReport(copro.id, copro.name)}
            disabled={isGeneratingReport}
          >
            {isGeneratingReport ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Génération en cours...
              </>
            ) : (
              'Télécharger le rapport'
            )}
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
    );
  };

  return (
    <div className="flex flex-col">
      <div className="mb-8 overflow-x-hidden rounded-lg bg-white p-4 shadow-md">
        <div className="mt-4 mb-12 items-center justify-between text-lg md:flex md:gap-x-3">
          <p>{description}</p>
          <button
            className="mt-4 flex w-full flex-row items-center justify-center gap-x-1 rounded-lg bg-gray-200 px-2 py-1 text-sm text-gray-600 hover:bg-gray-300 md:mt-0 md:w-auto"
            onClick={() => setIsSettingsOpen(true)}
          >
            <CogIcon className="inline h-8 w-8" />
            <div className="inline">
              Configuration
              <br />
              générale
            </div>
          </button>
        </div>
        {status === 'SUCCESS' && (
          <div className="my-10">
            <MUITable
              data={copros.filter((copro) => (!selectedCopro || (selectedCopro && selectedCopro.id === copro.id))).map((copro) => {
                const isExpanded = selectedCopro && selectedCopro.id === copro.id;
                return {
                  'N° IMMAT': copro.remoteId,
                  NOM: <div className="flex items-center">
                    <RedirectionCoproButtons coproId={copro.id} />
                    {copro.name}
                  </div>,
                  ADRESSE: <>
                    {copro.address !== null && copro.addresses[0]?.address ? (
                      <div className="flex items-center">
                        <div>
                          <RedirectionMapButtons address={`${copro.addresses[0]?.address} ${copro.addresses[0]?.postalCode} ${copro.addresses[0]?.city}`} />
                        </div>
                        <span>
                          {copro.addresses[0]?.address}
                          ,
                          {' '}
                          {copro.addresses[0]?.postalCode}
                          {' '}
                          {copro.addresses[0]?.city}
                        </span>
                      </div>
                    ) : ''}
                  </>,
                  'NB LOTS HAB / LOCAL PRO': copro.batchSize,
                  TYPE: coproTypes[copro.type] || 'Non spécifié',
                  'NB BATIMENTS': calculateValidatedBuildings(copro) > 0
                    ? calculateValidatedBuildings(copro)
                    : copro.details?.detailsDonneestechniquesNbbatimentstotal || 'N/A',
                  'SURFACE TOTALE (m²)': calculateTotalSurface(copro, true).toFixed(0),
                  'NB DPEs': copro.dpeCount || 0,
                  ACTIONS: (
                    <button
                      className="hover:bg-rgpd-blue-dark-dark align-center mr-2 flex w-full items-center justify-center rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md"
                      onClick={() => handleDetailsClick(copro)}
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUpIcon className="mr-2 h-5 w-5" />
                          {' '}
                          Revenir à la liste
                        </>
                      ) : (
                        <>
                          <ChevronDownIcon className="mr-2 h-5 w-5" />
                          {' '}
                          Voir les détails
                        </>
                      )}
                    </button>
                  ),
                };
              })}
              headers={headers}
              tableClass="min-w-full bg-white border text-sm"
              sortableColumns={sortableColumns}
              isSearchable={!selectedCopro}
              disablePagination={!!selectedCopro}
            />
            {selectedCopro && renderCoproDetails(selectedCopro)}
            {copros.length === 0 && (
              <div className="mx-10 my-10 text-center">
                <Badge mode="warning" text="Aucune copropriété enregistrée" />
              </div>
            )}
          </div>
        )}
        {status === 'ERROR' && (
          <div className="mx-10 my-32 text-center">
            <Badge mode="error" text={errorMessage} />
          </div>
        )}
        {status === 'PENDING' && (
          <div className="mx-10 my-32 text-center">
            <Spinner aria-label="Center-aligned spinner" size="xl" />
          </div>
        )}
      </div>
      <Popup
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        title="Présentation de l'entreprise pour les rapports"
      >
        <div className="p-2">
          <ReferentPresentationUploadList />
        </div>
      </Popup>
      <Popup
        isOpen={isPdfPreviewOpen}
        onClose={closePdfPreview}
        title={currentPdfPreviewTitle}
        width="90vw"
        height="90vh"
      >
        <div className="h-full w-full">
          <iframe
            src={currentPdfPreviewUrl}
            className="h-full w-full border-none"
            title="PDF Preview"
          />
        </div>
      </Popup>
    </div>
  );
};

export default ReportGenerator;
